import React, { useEffect, useState } from 'react';
import { ExternalLink, RefreshCw, Check, Image as ImageIcon, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_BASE = 'https://casaothers.nl';

/**
 * SharePreviewPanel
 *
 * Lets the admin verify — without any 3rd-party developer account — that
 * every blog article shares with its own correct featured image, title,
 * description, and canonical URL.
 *
 * How it works:
 *   1. Fetches every blog post (`/api/blog`)
 *   2. For each post, fetches the backend OG endpoint (`/api/og/blog/{slug}`)
 *      that social crawlers actually see
 *   3. Parses the returned <head> for og:title / og:image / og:description /
 *      og:url / og:type and renders a preview card
 *   4. Provides one-click deep links to free 3rd-party validators
 *      (LinkedIn Post Inspector, opengraph.xyz, socialsharepreview.com,
 *      metatags.io) — none require login for basic use.
 *
 * The panel does NOT depend on any external service to render its own
 * previews — so the admin always has an authoritative view of what social
 * platforms will see.
 */
const parseOgTags = (html) => {
  if (!html) return {};
  const grab = (attr, key) => {
    const re = new RegExp(`<meta\\s+${attr}="${key}"\\s+content="([^"]+)"`, 'i');
    const m = html.match(re);
    return m ? decodeHtml(m[1]) : '';
  };
  return {
    title: grab('property', 'og:title'),
    description: grab('property', 'og:description'),
    image: grab('property', 'og:image'),
    url: grab('property', 'og:url'),
    type: grab('property', 'og:type'),
    twitterCard: grab('name', 'twitter:card'),
    twitterImage: grab('name', 'twitter:image'),
  };
};

const decodeHtml = (s) => s
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&quot;/g, '"')
  .replace(/&#39;/g, "'");

const SharePreviewPanel = () => {
  const [posts, setPosts] = useState([]);
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const list = await fetch(`${API}/api/blog?limit=200`).then(r => r.json());
      const items = Array.isArray(list) ? list : (list.items || []);
      setPosts(items);
      // Fetch OG HTML for each blog in parallel
      const entries = await Promise.all(items.map(async (p) => {
        try {
          const html = await fetch(`${API}/api/og/blog/${p.slug}`).then(r => r.text());
          return [p.slug, parseOgTags(html)];
        } catch {
          return [p.slug, { error: true }];
        }
      }));
      setPreviews(Object.fromEntries(entries));
    } catch (e) {
      console.error('[SharePreview] load failed', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div style={styles.wrap} data-testid="share-preview-panel">
      <div style={styles.header}>
        <div>
          <h2 style={styles.h2}>Deel-preview validatie</h2>
          <p style={styles.subtitle}>
            Zo ziet Facebook, LinkedIn, WhatsApp en X elke blog. Klik op de externe validators om te dubbelchecken.
          </p>
        </div>
        <button type="button" onClick={load} disabled={refreshing} style={styles.refreshBtn} data-testid="share-preview-refresh">
          <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
          {refreshing ? 'Verversen…' : 'Vernieuwen'}
        </button>
      </div>

      {/* Legenda + externe validators (universeel — niet blog-specifiek) */}
      <div style={styles.legend}>
        <strong style={styles.legendTitle}>Externe validators zonder Facebook-account nodig:</strong>
        <div style={styles.legendLinks}>
          <a href="https://www.linkedin.com/post-inspector/" target="_blank" rel="noopener noreferrer" style={styles.pill}>
            LinkedIn Post Inspector <ExternalLink size={12} />
          </a>
          <a href="https://www.opengraph.xyz/" target="_blank" rel="noopener noreferrer" style={styles.pill}>
            opengraph.xyz <ExternalLink size={12} />
          </a>
          <a href="https://socialsharepreview.com/" target="_blank" rel="noopener noreferrer" style={styles.pill}>
            socialsharepreview.com <ExternalLink size={12} />
          </a>
          <a href="https://metatags.io/" target="_blank" rel="noopener noreferrer" style={styles.pill}>
            metatags.io <ExternalLink size={12} />
          </a>
        </div>
      </div>

      {loading ? (
        <p style={styles.loading}>Blogs laden…</p>
      ) : (
        <div style={styles.grid}>
          {posts.map((post) => {
            const og = previews[post.slug] || {};
            const canonical = `${PUBLIC_BASE}/blog/${post.slug}`;
            const encoded = encodeURIComponent(canonical);
            const distinctImage = og.image && !og.image.includes('/og-image.jpg');
            const hasError = og.error || !og.title || !og.image;

            return (
              <div key={post.slug} style={styles.card} data-testid={`share-card-${post.slug}`}>
                {/* Live preview identical to what Facebook renders */}
                <div style={styles.previewFrame}>
                  {og.image ? (
                    <img
                      src={og.image}
                      alt={og.title || post.title}
                      style={styles.previewImage}
                      loading="lazy"
                    />
                  ) : (
                    <div style={styles.previewImagePlaceholder}>
                      <ImageIcon size={24} color="#8C8B88" />
                    </div>
                  )}
                  <div style={styles.previewBody}>
                    <span style={styles.previewDomain}>casaothers.nl</span>
                    <strong style={styles.previewTitle}>{og.title || <em>(geen titel)</em>}</strong>
                    <span style={styles.previewDescription}>{og.description || <em>(geen beschrijving)</em>}</span>
                  </div>
                </div>

                {/* Diagnostic strip */}
                <div style={styles.diagWrap}>
                  <div style={styles.diagRow}>
                    {hasError ? (
                      <span style={styles.badError} data-testid={`share-status-${post.slug}`}>
                        <AlertTriangle size={12} /> Ontbrekende OG-data
                      </span>
                    ) : distinctImage ? (
                      <span style={styles.badOk} data-testid={`share-status-${post.slug}`}>
                        <Check size={12} /> Blog-specifieke afbeelding
                      </span>
                    ) : (
                      <span style={styles.badWarn} data-testid={`share-status-${post.slug}`}>
                        <AlertTriangle size={12} /> Fallback afbeelding
                      </span>
                    )}
                    {og.type && <span style={styles.badNeutral}>type: {og.type}</span>}
                  </div>

                  <ul style={styles.metaList}>
                    <li><strong>URL:</strong> <a href={canonical} target="_blank" rel="noopener noreferrer" style={styles.metaLink}>{canonical}</a></li>
                    <li><strong>og:image:</strong> <span style={styles.metaValue}>{og.image ? og.image.slice(0, 90) + (og.image.length > 90 ? '…' : '') : '(leeg)'}</span></li>
                  </ul>
                </div>

                {/* Cross-validation buttons */}
                <div style={styles.actions}>
                  <a
                    href={`https://www.linkedin.com/post-inspector/inspect/${encoded}`}
                    target="_blank" rel="noopener noreferrer"
                    style={styles.actionBtn}
                    data-testid={`share-validate-linkedin-${post.slug}`}
                  >
                    LinkedIn Inspector <ExternalLink size={11} />
                  </a>
                  <a
                    href={`https://www.opengraph.xyz/url/${encoded}`}
                    target="_blank" rel="noopener noreferrer"
                    style={styles.actionBtn}
                    data-testid={`share-validate-og-${post.slug}`}
                  >
                    opengraph.xyz <ExternalLink size={11} />
                  </a>
                  <a
                    href={`https://metatags.io/?url=${encoded}`}
                    target="_blank" rel="noopener noreferrer"
                    style={styles.actionBtn}
                    data-testid={`share-validate-metatags-${post.slug}`}
                  >
                    metatags.io <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: { padding: '1rem 0' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', margin: '0 0 0.3rem', color: '#1A1918' },
  subtitle: { color: '#585755', fontSize: '0.88rem', margin: 0, maxWidth: '640px', lineHeight: 1.4 },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: '#1A1918', color: '#FAF9F6', border: 'none', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer' },
  legend: { backgroundColor: '#FAF9F6', border: '1px solid #EAE5DD', padding: '0.8rem 1rem', marginBottom: '1.25rem' },
  legendTitle: { display: 'block', fontSize: '0.78rem', color: '#585755', marginBottom: '0.5rem', letterSpacing: '0.03em' },
  legendLinks: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' },
  pill: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.7rem', border: '1px solid #C5A059', color: '#1A1918', textDecoration: 'none', fontSize: '0.75rem', fontWeight: 600, backgroundColor: 'white' },
  loading: { color: '#585755', fontSize: '0.9rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.25rem' },
  card: { border: '1px solid #EAE5DD', backgroundColor: 'white', display: 'flex', flexDirection: 'column' },
  previewFrame: { display: 'flex', flexDirection: 'column', borderBottom: '1px solid #EAE5DD' },
  previewImage: { width: '100%', height: '180px', objectFit: 'cover', display: 'block' },
  previewImagePlaceholder: { width: '100%', height: '180px', backgroundColor: '#F0EEE9', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  previewBody: { padding: '0.75rem 0.9rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', backgroundColor: '#F7F5F1' },
  previewDomain: { fontSize: '0.7rem', color: '#8C8B88', textTransform: 'uppercase', letterSpacing: '0.06em' },
  previewTitle: { fontSize: '0.95rem', color: '#1A1918', lineHeight: 1.3, fontWeight: 600 },
  previewDescription: { fontSize: '0.78rem', color: '#585755', lineHeight: 1.4 },
  diagWrap: { padding: '0.7rem 0.9rem', fontSize: '0.78rem', color: '#585755' },
  diagRow: { display: 'flex', gap: '0.4rem', marginBottom: '0.5rem', flexWrap: 'wrap' },
  badOk: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: '#E5F1E7', color: '#1F5D2E', padding: '0.2rem 0.5rem', fontSize: '0.7rem', fontWeight: 600 },
  badWarn: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: '#FFF4E5', color: '#8C5A15', padding: '0.2rem 0.5rem', fontSize: '0.7rem', fontWeight: 600 },
  badError: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', backgroundColor: '#F7E5E2', color: '#8A2A1F', padding: '0.2rem 0.5rem', fontSize: '0.7rem', fontWeight: 600 },
  badNeutral: { display: 'inline-flex', alignItems: 'center', backgroundColor: '#EAE5DD', color: '#585755', padding: '0.2rem 0.5rem', fontSize: '0.7rem', fontWeight: 500 },
  metaList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.73rem' },
  metaLink: { color: '#C5A059', textDecoration: 'none', wordBreak: 'break-all' },
  metaValue: { color: '#585755', wordBreak: 'break-all' },
  actions: { display: 'flex', gap: '0.35rem', flexWrap: 'wrap', padding: '0.5rem 0.9rem 0.9rem' },
  actionBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.7rem', border: '1px solid #EAE5DD', backgroundColor: 'white', color: '#585755', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'none', cursor: 'pointer' },
};

export default SharePreviewPanel;
